import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Divider, Chip, Stepper, Step, StepLabel, InputAdornment, List,
  ListItemButton, ListItemText, Tooltip,
} from '@mui/material';
import {
  Search, Building2, Globe, Rocket, Check, Flag, Copy, ExternalLink, FileText, Smartphone,
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const SectionCard = styled(Paper)(({ theme }) => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const formatDomain = (input) =>
  (input || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 30);

const formatUSPhone = (v) => {
  const n = (v || '').replace(/\D/g, '').substring(0, 10);
  if (n.length === 0) return '';
  if (n.length <= 3) return `(${n}`;
  if (n.length <= 6) return `(${n.slice(0, 3)}) ${n.slice(3)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

function statusColor(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('active') || s.includes('good standing')) return '#10B981';
  if (s.includes('forfeit') || s.includes('inactive') || s.includes('dissolved') || s.includes('cancel')) return '#EF4444';
  return '#F59E0B';
}

export default function Gringa() {
  const navigate = useNavigate();

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();

  // Step 1 — search
  const [query, setQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searchError, setSearchError] = useState('');
  const debounceRef = useRef(null);

  // Step 2 — selected company + form
  const [selected, setSelected] = useState(null); // resumo da lista
  const [companyData, setCompanyData] = useState(null); // detalhes completos da API
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [formData, setFormData] = useState({ phone: '', email: '', dominio: '' });

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [createdUrl, setCreatedUrl] = useState('');
  const [createdPdfUrl, setCreatedPdfUrl] = useState('');

  // Auto-search com debounce a cada 350ms se 2+ chars
  useEffect(() => {
    if (selected) return; // não busca mais quando já selecionou
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setSearchError('');
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError('');
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/sites/company/us/search`,
          {
            params: { q: query.trim() },
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000,
          }
        );
        if (res.data.success) {
          setResults(res.data.results || []);
        } else {
          setSearchError(res.data.message || 'Search failed.');
        }
      } catch (err) {
        setSearchError(err.response?.data?.message || 'Search failed. Try again.');
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, selected]);

  if (user.tipo !== 'admin') {
    return (
      <Fade in timeout={400}>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">This area is restricted to administrators.</Alert>
          <Button onClick={() => navigate('/dashboard')} sx={{ mt: 2 }} variant="contained">
            Back
          </Button>
        </Box>
      </Fade>
    );
  }

  const activeStep = !companyData ? 0 : createdUrl ? 2 : 1;

  const handleSelect = async (item) => {
    setSelected(item);
    setSearchError('');
    setDetailsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sites/company/us/${item.jurisdiction_code}/${encodeURIComponent(item.company_number)}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }
      );
      if (res.data.success) {
        const d = res.data.data;
        setCompanyData(d);
        setFormData((prev) => ({
          ...prev,
          dominio: prev.dominio || formatDomain(d.trade_name || d.legal_name || ''),
        }));
      } else {
        setError(res.data.message || 'Could not load company details.');
        setSelected(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load company details.');
      setSelected(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleReset = () => {
    setSelected(null);
    setCompanyData(null);
    setCreatedUrl('');
    setCreatedPdfUrl('');
    setError('');
  };

  const handleGenerate = async () => {
    setError('');
    if (!formData.dominio) { setError('Subdomain is required.'); return; }
    if (!formData.phone) { setError('Phone is required.'); return; }
    if (!formData.email) { setError('Email is required.'); return; }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sites/generate-gringa`,
        {
          companyData,
          phone: formData.phone,
          email: formData.email,
          dominio: formData.dominio,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 }
      );
      if (res.data.success) {
        setCreatedUrl(res.data.url);
        setCreatedPdfUrl(res.data.pdfUrl || '');
      } else {
        setError(res.data.message || 'Failed to generate site.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate site.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 980, mx: 'auto', p: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Flag size={28} color="#3B82F6" />
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
              GRINGA
            </Typography>
            <Chip label="Admin" size="small" sx={{ background: 'rgba(59,130,246,0.2)', color: '#60A5FA', fontWeight: 700 }} />
            <Chip label="🇺🇸 US" size="small" sx={{ background: 'rgba(255,255,255,0.06)', color: '#E5E7EB', fontWeight: 700 }} />
          </Box>
          <Typography sx={{ color: '#9CA3AF' }}>
            Generate a verification site (en-US) on <strong style={{ color: '#60A5FA' }}>*.verificaix.com</strong> for U.S.
            companies, plus a downloadable Business Registration PDF — ready to upload at Meta Business Manager.
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          <Step><StepLabel>Find company</StepLabel></Step>
          <Step><StepLabel>Generate</StepLabel></Step>
          <Step><StepLabel>Done</StepLabel></Step>
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Step 1 — Search */}
        <SectionCard>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
            <Building2 size={20} color="#60A5FA" />
            <Typography variant="h6" sx={{ color: '#fff' }}>1. Search the company</Typography>
          </Box>

          {!companyData ? (
            <>
              <TextField
                label="Company name"
                placeholder="e.g. Apple Inc."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#9CA3AF" />
                    </InputAdornment>
                  ),
                  endAdornment: searchLoading && (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ),
                }}
                helperText="Type at least 2 characters. Results appear automatically."
              />

              {searchError && <Alert severity="warning" sx={{ mt: 2 }}>{searchError}</Alert>}

              {results.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2, maxHeight: 420, overflow: 'auto',
                    background: '#0B0F1A', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2,
                  }}
                >
                  <List dense disablePadding>
                    {results.map((r, i) => (
                      <ListItemButton
                        key={`${r.jurisdiction_code}-${r.company_number}-${i}`}
                        onClick={() => handleSelect(r)}
                        sx={{
                          py: 1.4, px: 2,
                          borderBottom: i < results.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                          '&:hover': { background: 'rgba(59,130,246,0.06)' },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                                {r.name}
                              </Typography>
                              {r.state_abbr && (
                                <Chip
                                  size="small"
                                  label={`🏛 ${r.state_abbr}`}
                                  sx={{ height: 20, fontSize: '0.7rem', background: 'rgba(255,255,255,0.06)', color: '#D1D5DB' }}
                                />
                              )}
                              <Chip
                                size="small"
                                label={r.status || 'Unknown'}
                                sx={{
                                  height: 20, fontSize: '0.7rem', fontWeight: 700,
                                  background: `${statusColor(r.status)}22`,
                                  color: statusColor(r.status),
                                }}
                              />
                              {r.formation_date && (
                                <Typography sx={{ color: '#6B7280', fontSize: '0.72rem' }}>
                                  · since {fmtDate(r.formation_date)}
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem', mt: 0.3 }}>
                              {r.jurisdiction_label}
                              {r.address && ` · ${typeof r.address === 'string' ? r.address : ''}`}
                              {r.company_type && ` · ${r.company_type}`}
                              {` · #${r.company_number}`}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}

              {!searchLoading && query.trim().length >= 2 && results.length === 0 && !searchError && (
                <Typography sx={{ color: '#6B7280', mt: 2, fontSize: '0.85rem' }}>
                  No matches. Try typing the full legal name.
                </Typography>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Check size={16} color="#10B981" />
                    <Typography sx={{ color: '#fff', fontWeight: 700 }}>{companyData.legal_name}</Typography>
                    <Chip
                      size="small"
                      label={companyData.status || 'Unknown'}
                      sx={{
                        height: 20, fontSize: '0.7rem', fontWeight: 700,
                        background: `${statusColor(companyData.status)}22`,
                        color: statusColor(companyData.status),
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.83rem' }}>
                    {companyData.tax_id && `#${companyData.tax_id} · `}
                    {companyData.jurisdiction_label}
                    {companyData.address?.city && ` · ${companyData.address.city}, ${companyData.address.state}`}
                    {companyData.formation_date && ` · since ${fmtDate(companyData.formation_date)}`}
                  </Typography>
                </Box>
              </Box>
              <Button variant="outlined" onClick={handleReset} sx={{ minHeight: 48 }}>
                Change company
              </Button>
            </Box>
          )}

          {detailsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={18} />
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Loading details from public registry…</Typography>
            </Box>
          )}
        </SectionCard>

        {/* Step 2 — Form */}
        {companyData && !createdUrl && (
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
              <Rocket size={20} color="#60A5FA" />
              <Typography variant="h6" sx={{ color: '#fff' }}>2. Generate site</Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Phone"
                placeholder="(415) 555-0123"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: formatUSPhone(e.target.value) })}
                helperText="US format. We add +1 automatically."
              />
              <TextField
                label="Email"
                placeholder="contact@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <TextField
                label="Subdomain"
                placeholder="my-company"
                value={formData.dominio}
                onChange={(e) => setFormData({ ...formData, dominio: formatDomain(e.target.value) })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Globe size={16} color="#9CA3AF" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>.verificaix.com</Typography>
                    </InputAdornment>
                  ),
                }}
                helperText={formData.dominio ? `URL: https://${formData.dominio}.verificaix.com` : 'Lowercase letters, numbers and hyphens.'}
                sx={{ gridColumn: { sm: '1 / -1' } }}
              />
            </Box>

            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <FileText size={14} /> A Business Registration PDF will be generated automatically.
              </Typography>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={creating}
                sx={{ minHeight: 50, px: 4, fontWeight: 700 }}
              >
                {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate site + PDF'}
              </Button>
            </Box>
          </SectionCard>
        )}

        {/* Step 3 — Success */}
        {createdUrl && (
          <SectionCard>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{
                width: 64, height: 64, mx: 'auto', borderRadius: '50%',
                background: 'rgba(16,185,129,0.15)', display: 'grid', placeItems: 'center', mb: 2,
              }}>
                <Check size={32} color="#10B981" />
              </Box>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>Site generated successfully</Typography>
              <Typography sx={{ color: '#9CA3AF', mb: 3 }}>
                Available now with SSL. PDF can be sent to Meta as a Business registration document.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1,
                  background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: 2, px: 2, py: 1,
                }}>
                  <Globe size={16} color="#60A5FA" />
                  <Typography sx={{ color: '#fff', fontWeight: 700 }}>{createdUrl}</Typography>
                  <Tooltip title="Copy">
                    <Button size="small" sx={{ minWidth: 0, p: 0.5, ml: 0.5, color: '#9CA3AF' }} onClick={() => navigator.clipboard.writeText(createdUrl)}>
                      <Copy size={14} />
                    </Button>
                  </Tooltip>
                </Box>
                {createdPdfUrl && (
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
                    borderRadius: 2, px: 2, py: 1,
                  }}>
                    <FileText size={16} color="#10B981" />
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem' }}>{createdPdfUrl}</Typography>
                    <Tooltip title="Copy PDF link">
                      <Button size="small" sx={{ minWidth: 0, p: 0.5, ml: 0.5, color: '#9CA3AF' }} onClick={() => navigator.clipboard.writeText(createdPdfUrl)}>
                        <Copy size={14} />
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<ExternalLink size={16} />}
                  href={createdUrl}
                  target="_blank"
                  rel="noopener"
                >
                  Open site
                </Button>
                {createdPdfUrl && (
                  <Button
                    variant="outlined"
                    startIcon={<FileText size={16} />}
                    href={createdPdfUrl}
                    target="_blank"
                    rel="noopener"
                    sx={{ borderColor: '#10B981', color: '#10B981', '&:hover': { borderColor: '#059669', background: 'rgba(16,185,129,0.06)' } }}
                  >
                    Download PDF
                  </Button>
                )}
                <Button onClick={() => navigate('/meus-sites')} variant="text">
                  Go to My Sites
                </Button>
              </Box>

              <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem', mb: 1.5 }}>
                  Need to confirm Meta BM by SMS?
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Smartphone size={16} />}
                  onClick={() => navigate('/numero-virtual?country=us')}
                  sx={{
                    background: '#1877F2',
                    '&:hover': { background: '#1565C0' },
                    fontWeight: 700,
                    px: 3,
                  }}
                >
                  Get US phone number for Facebook SMS
                </Button>
                <Typography sx={{ color: '#6B7280', fontSize: '0.75rem', mt: 1 }}>
                  +1 number · 30 coins · code arrives in up to 15 min
                </Typography>
              </Box>
            </Box>
          </SectionCard>
        )}
      </Box>
    </Fade>
  );
}
